import type { EventItem, CourseItem } from '@tc8l/nuxt-overview'

// TODO: Venue data should come from the API
const venue = {
  name: 'The Cre8ion.Lab',
  url: 'https://www.cre8ion.com/',
  address: {
    streetAddress: 'Veemarktkade 8',
    city: "'s-Hertogenbosch",
    postalCode: '5222 AE',
  },
}

function stripDescription(description?: string): string | undefined {
  return description ? description.replace(/<[^>]*>/g, '') : undefined
}

export function useStructuredData() {
  function forEvent(event: EventItem) {
    const programs = event.programs ?? []

    // TODO : Performer data should be added and should come from the API. Depends from where per venue & often isn't used
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': programs.map((program) => {
        const prices = program.prices ?? []

        return {
          '@type': 'Event',
          name: event.title,
          startDate: program.startAt,
          endDate: program.endAt,
          eventStatus: program.isCanceled ? 'https://schema.org/EventCancelled' : 'https://schema.org/EventScheduled',
          eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
          image: event.image1?.url,
          description: stripDescription(event.description),
          location: {
            '@type': 'Place',
            name: venue.name,
            address: {
              '@type': 'PostalAddress',
              streetAddress: venue.address.streetAddress,
              addressLocality: venue.address.city,
              postalCode: venue.address.postalCode,
              addressCountry: 'NL',
            },
          },
          offers: prices.map((price) => ({
            '@type': 'Offer',
            url: event.button?.link,
            price: price.price,
            priceCurrency: 'EUR',
            availability: price.availability === 0 ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
          })),
          organizer: {
            '@type': 'Organization',
            name: venue.name,
            url: venue.url,
            address: {
              '@type': 'PostalAddress',
              streetAddress: venue.address.streetAddress,
              addressLocality: venue.address.city,
              postalCode: venue.address.postalCode,
              addressCountry: 'NL',
            },
          },
        }
      }),
    })
  }

  function forCourse(course: CourseItem) {
    const variants = course.variants ?? []

    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: course.title,
      url: course.url,
      image: course.image1?.url,
      description: stripDescription(course.description),
      provider: {
        '@type': 'Organization',
        name: venue.name,
        sameAs: venue.url,
      },
      hasCourseInstance: variants.map((variant) => {
        const prices = variant.prices ?? []

        return {
          '@type': 'CourseInstance',
          courseMode: 'onsite',
          startDate: variant.startAt,
          endDate: variant.endAt,
          location: {
            '@type': 'Place',
            name: venue.name,
            address: {
              '@type': 'PostalAddress',
              streetAddress: venue.address.streetAddress,
              addressLocality: venue.address.city,
              postalCode: venue.address.postalCode,
              addressCountry: 'NL',
            },
          },
          instructor: variant.teachers?.map((teacher) => ({
            '@type': 'Person',
            name: teacher.name,
          })),
          offers: prices.map((price) => ({
            '@type': 'Offer',
            url: course.button?.link,
            price: price.price,
            priceCurrency: 'EUR',
            availability: price.availability === 0 ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
          })),
        }
      }),
    })
  }

  function forCoursesList(courses: CourseItem[]) {
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: courses.map((course, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Course',
          url: course.url,
          name: course.title,
          description: stripDescription(course.description),
          provider: {
            '@type': 'Organization',
            name: venue.name,
            sameAs: venue.url,
          },
        },
      })),
    })
  }

  // TODO : Check if 'director' is given by the API
  function forFilm(event: EventItem) {
    const programs = event.programs ?? []

    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Movie',
      '@id': event.url,
      name: event.title,
      description: stripDescription(event.description),
      image: event.image1?.url,
      // director: {
      //     "@type": "Person",
      //     name:
      // },
      genre: event.genres.map((genre) => genre.name),
      duration: event.fields?.duration,
      contentRating: event.fields?.classifications,
      actor: event.fields?.cast.map((actor) => {
        return {
          '@type': 'Person',
          name: actor,
        }
      }),
      dateCreated: event.fields?.releaseDate,

      subjectOf: programs.map((program) => {
        const prices = program.prices ?? []

        return {
          '@type': 'ScreeningEvent',
          workPresented: {
            '@type': 'Movie',
            '@id': event.url,
          },

          startDate: program.startAt,
          endDate: program.endAt,
          url: program.button?.link,
          location: {
            '@type': 'Place',
            name: venue.name,
            address: {
              '@type': 'PostalAddress',
              streetAddress: venue.address.streetAddress,
              addressLocality: venue.address.city,
              postalCode: venue.address.postalCode,
              addressCountry: 'NL',
            },
          },
          offers: prices?.map((price) => ({
            '@type': 'Offer',
            url: program.button?.link,
            price: price.price,
            priceCurrency: 'EUR',
            availability: price.availability === 0 ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
          })),
        }
      }),
    })
  }

  return {
    forEvent,
    forCourse,
    forCoursesList,
    forFilm,
  }
}
